/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from "react-i18next";

import GridOdd from 'shared/components/outcomes/grids/GridOdd';

/**
 * Renders the Exact Score outcomes for football
 * Grid of possible scores from 0-0 to 5-5
 *
 * @class  ExactScore (name)
 * @param  {Object}   props The properties
 * @return {JSX}
 */
function ExactScore(props) {
  const { t } = useTranslation();
  const {
    outcomes = {},
    homeTeamName = 'Home',
    awayTeamName = 'Away',
    event,
    orderByResult = false,
    oddVariant = 'grid-odd-football',
  } = props;

  const sportColor = 'var(--sport-color)';

  // Filter for market type 5004 (Exact Score)
  const esOutcomes = Object.values(outcomes)
    .filter(x => parseInt(x.market_type_id) === 5004);

  if (orderByResult) {
    const parsed = esOutcomes
      .map((o) => {
        const raw = String(o.outcome || '');
        const [hRaw, aRaw] = raw.split('-');
        const h = Number(hRaw);
        const a = Number(aRaw);
        const valid = Number.isFinite(h) && Number.isFinite(a);
        const bucket = !valid ? 3 : (h > a ? 0 : h === a ? 1 : 2); // 1, X, 2
        const sortOrder = valid ? (h * 100 + a) : 999999;
        return { o, raw, h, a, valid, bucket, sortOrder };
      })
      .filter((x) => x.valid);

    const groups = {
      home: parsed.filter((x) => x.bucket === 0).sort((a, b) => a.sortOrder - b.sortOrder),
      draw: parsed.filter((x) => x.bucket === 1).sort((a, b) => a.sortOrder - b.sortOrder),
      away: parsed.filter((x) => x.bucket === 2).sort((a, b) => a.sortOrder - b.sortOrder),
    };

    const Section = ({ title, items }) => (
      <div className="mb-4">
        <div
          className="px-3 py-2 text-xs font-semibold border-b"
          style={{ backgroundColor: '#1a3a5f', borderColor: '#1a3a5f', color: '#ffd03a' }}
        >
          {title}
        </div>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 p-3">
          {items.map(({ o, raw }) => (
            <GridOdd
              key={o.outcome_id}
              {...{
                ...o,
                class_names: [oddVariant, 'grid-odd-compact', 'bg-background/80'],
                market_type_group_id: 511,
                display_label: raw,
                event,
              }}
            />
          ))}
        </div>
      </div>
    );

    return (
      <div className="exact-score-ordered">
        <Section title={`1 · ${homeTeamName}`} items={groups.home} />
        <Section title={`X · ${t('DRAW')}`} items={groups.draw} />
        <Section title={`2 · ${awayTeamName}`} items={groups.away} />
      </div>
    );
  }

  // Create outcome map for quick lookup
  const outcomeMap = {};
  esOutcomes.forEach(o => {
    outcomeMap[o.outcome] = o;
  });

  // Generate grid for scores 0-0 to maxGoals-maxGoals (required from props)
  const maxGoalsRaw = props.maxGoals;
  if (maxGoalsRaw == null || !Number.isFinite(Number(maxGoalsRaw))) return null;
  const maxGoals = Number(maxGoalsRaw);
  const homeGoalsRange = Array.from({ length: maxGoals + 1 }, (_, i) => i);
  const awayGoalsRange = Array.from({ length: maxGoals + 1 }, (_, i) => i);

  return (
    <div className="exact-score-grid overflow-x-auto">
      {/* Header Row - Away team goals */}
      <div className="flex flex-row min-w-max">
        <div 
          className="w-12 h-10 flex items-center justify-center text-xs font-semibold text-white"
          style={{ backgroundColor: sportColor }}
        >
          <span className="truncate">{awayTeamName.substring(0, 3)}</span>
        </div>
        {awayGoalsRange.map(awayGoals => (
          <div 
            key={`header-${awayGoals}`}
            className="w-14 h-10 flex items-center justify-center text-xs font-semibold text-white"
            style={{ backgroundColor: sportColor }}
          >
            {awayGoals}
          </div>
        ))}
      </div>

      {/* Score Grid Rows */}
      {homeGoalsRange.map((homeGoals, rowIndex) => {
        const isEvenRow = rowIndex % 2 === 1;
        return (
          <div 
            key={`row-${homeGoals}`}
            className={`flex flex-row min-w-max ${isEvenRow ? 'bg-gray-50' : ''}`}
          >
            {/* Row header - Home team goals */}
            <div 
              className="w-12 h-12 flex items-center justify-center text-xs font-semibold text-white"
              style={{ backgroundColor: sportColor }}
            >
              {homeGoals}
            </div>
            
            {/* Score cells */}
            {awayGoalsRange.map(awayGoals => {
              const score = `${homeGoals}-${awayGoals}`;
              const outcome = outcomeMap[score];
              
              return (
                <div 
                  key={score}
                  className="w-14 h-12 flex items-center justify-center p-0.5"
                >
                  {outcome ? (
                    <GridOdd
                      {...{
                        ...outcome,
                        class_names: [oddVariant, 'grid-odd-compact', 'bg-background/80'],
                        market_type_group_id: 511,
                        event
                        // No display_label needed - grid headers already show the score
                      }}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Legend */}
      <div className="mt-2 px-2 py-1 text-xs text-gray-500">
        <span className="font-semibold">{homeTeamName}</span> {t('GOALS')} ↓ | <span className="font-semibold">{awayTeamName}</span> {t('GOALS')} →
      </div>
    </div>
  );
}

export default ExactScore;

